import React, { useCallback, useEffect } from 'react';
import { FormApi } from 'final-form';
import { Field } from 'react-final-form';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import SelectInput from '../../components/formFields/SelectInput';
import useAppTypeConfigs from '../../hooks/appTypeBasedConfigs';
import { clearDistrictList, fetchDistrictListRequest } from '../../store/district/actions';
import { clearChiefdomList, fetchChiefdomListRequest } from '../../store/chiefdom/actions';
import { districtLoadingSelector, getDistrictListSelector } from '../../store/district/selectors';
import { getChiefdomListSelector, chiefdomLoadingSelector } from '../../store/chiefdom/selectors';
import { divisionLoadingSelector, getDivisionListSelector } from '../../store/division/selectors';
import { fetchDivisionListRequest } from '../../store/division/actions';
import { toNumericId } from '../../utils/commonUtils';

export interface IRegionFiltersFormValues {
  division?: any;
  district?: any;
  upazila?: any;
}

interface IRegionFilterFieldsProps {
  form: FormApi<any>;
  name: string;
  values: any;
}

interface IMatchParams {
  regionId: string;
}

const RegionFilterFields = ({
  form,
  name,
  values
}: IRegionFilterFieldsProps): React.ReactElement => {
  const { regionId: countryId } = useParams<IMatchParams>();
  const dispatch = useDispatch();
  const districtList = useSelector(getDistrictListSelector);
  const districtLoading = useSelector(districtLoadingSelector);
  const chiefdomList = useSelector(getChiefdomListSelector);
  const chiefdomLoading = useSelector(chiefdomLoadingSelector);
  const divisionList = useSelector(getDivisionListSelector);
  const divisionLoading = useSelector(divisionLoadingSelector);

  const {
    division: { s: divisionSName },
    district: { s: districtSName },
    chiefdom: { s: chiefdomSName }
  } = useAppTypeConfigs();

  const fieldName = useCallback((field: keyof IRegionFiltersFormValues) => `${name}.${field}`, [name]);
  const formValues: IRegionFiltersFormValues = values?.[name] || {};
  const divisionId = toNumericId(formValues.division);
  const districtId = toNumericId(formValues.district);

  useEffect(() => {
    if (countryId) {
      dispatch(
        fetchDivisionListRequest({
          countryId: Number(countryId)
        })
      );
    }
  }, [countryId, dispatch]);

  // `form` is intentionally excluded because its reference changes on re-renders,
  // which causes unnecessary effect executions and duplicate API calls.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    form.change(fieldName('district'), null);
    form.change(fieldName('upazila'), null);

    if (!divisionId) {
      dispatch(clearDistrictList());
      dispatch(clearChiefdomList());
      return;
    }

    dispatch(
      fetchDistrictListRequest({
        divisionId: Number(divisionId)
      })
    );
  }, [divisionId, dispatch, fieldName]);

  useEffect(() => {
    form.change(fieldName('upazila'), null);

    if (!districtId) {
      dispatch(clearChiefdomList());
      return;
    }

    dispatch(
      fetchChiefdomListRequest({
        districtId
      })
    );
  }, [districtId, dispatch, fieldName]);

  return (
    <>
      <Field
        name={fieldName('division')}
        render={({ input }) => (
          <div className='py-dot625'>
            <SelectInput
              label={divisionSName}
              loadingOptions={divisionLoading}
              options={divisionList}
              labelKey='name'
              valueKey='id'
              value={input.value}
              required={false}
              onChange={input.onChange}
            />
          </div>
        )}
      />
      <Field
        name={fieldName('district')}
        render={({ input }) => (
          <div className='py-dot625'>
            <SelectInput
              label={districtSName}
              loadingOptions={districtLoading}
              options={districtList}
              labelKey='name'
              valueKey='id'
              value={input.value}
              required={false}
              onChange={input.onChange}
            />
          </div>
        )}
      />
      <Field
        name={fieldName('upazila')}
        render={({ input }) => (
          <div className='py-dot625'>
            <SelectInput
              label={chiefdomSName}
              loadingOptions={chiefdomLoading}
              options={chiefdomList}
              labelKey='name'
              valueKey='id'
              value={input.value}
              required={false}
              onChange={input.onChange}
            />
          </div>
        )}
      />
    </>
  );
};

export default RegionFilterFields;
